/**
 * DICOM `BurnedInAnnotation` (0028,0301): whether identifying information is
 * *burned into the image pixels themselves* — e.g. a patient name, ID, or date
 * rendered into the greyscale — as opposed to living only in DICOM metadata.
 *
 * This is the pixel-level PHI signal, and it is distinct from metadata
 * de-identification (`PatientIdentityRemoved` / the Basic Application Level
 * Confidentiality Profile): per DICOM PS3.15 the Basic Profile does NOT clean
 * burned-in pixels (that is the separate "Clean Pixel Data" option). So any
 * feature that renders the image pixels verbatim (the viewport screenshot) must
 * consult this attribute before treating those pixels as safe to share.
 *
 * The attribute is Type 3 (optional) and therefore frequently absent; an absent
 * or unrecognised value is {@link BurnedInAnnotationStatus.Unknown} — "cannot be
 * affirmed either way" — and must never be silently treated as safe.
 */
export enum BurnedInAnnotationStatus {
    /** (0028,0301) == NO — the source affirms it carries no burned-in identifiers. */
    No = 'no',
    /** (0028,0301) == YES — the pixel data contains burned-in identifiers. */
    Yes = 'yes',
    /** Absent, empty, or unrecognised — safety cannot be affirmed. */
    Unknown = 'unknown',
}

/**
 * Classify one raw `BurnedInAnnotation` value. Matching is case-insensitive and
 * whitespace-tolerant; only the DICOM enumerated values `"YES"`/`"NO"` are
 * recognised — everything else, including an empty or absent value, is
 * {@link BurnedInAnnotationStatus.Unknown} (never silently "safe").
 */
export function parseBurnedInAnnotation(
    raw: string | undefined
): BurnedInAnnotationStatus {
    switch (raw?.trim().toUpperCase()) {
        case 'YES':
            return BurnedInAnnotationStatus.Yes;
        case 'NO':
            return BurnedInAnnotationStatus.No;
        default:
            return BurnedInAnnotationStatus.Unknown;
    }
}

/**
 * Combine the per-source statuses of everything composited into one rendered
 * image (all slices of a series, or every dataset drawn into a fused viewport)
 * into a single fail-safe verdict, with precedence **Yes > Unknown > No**:
 *
 * - any source declaring YES makes the result {@link BurnedInAnnotationStatus.Yes};
 * - otherwise any source that cannot be affirmed (Unknown), or an empty set,
 *   makes the result {@link BurnedInAnnotationStatus.Unknown};
 * - only when *every* source affirmatively declares NO is the result
 *   {@link BurnedInAnnotationStatus.No}.
 *
 * A region is thus called "safe" (No) only when all of its sources say so — one
 * unknown source is enough to withhold that guarantee.
 */
export function aggregateBurnedInAnnotation(
    statuses: Iterable<BurnedInAnnotationStatus>
): BurnedInAnnotationStatus {
    let sawAny = false;
    let sawUnknown = false;
    for (const status of statuses) {
        sawAny = true;
        if (status === BurnedInAnnotationStatus.Yes) {
            return BurnedInAnnotationStatus.Yes;
        }
        if (status === BurnedInAnnotationStatus.Unknown) {
            sawUnknown = true;
        }
    }
    return sawAny && !sawUnknown
        ? BurnedInAnnotationStatus.No
        : BurnedInAnnotationStatus.Unknown;
}
